import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { paths } from "@/app/routes/paths";
import { isTripOrder, type Order } from "@/entities/order";
import { colors } from "@/shared/design-system";
import { formatXof } from "@/shared/format/currency";
import { AppText, Card } from "@/shared/ui";

/**
 * Courses récentes — historique consultable de la colonne contextuelle.
 *
 * Ne fait pas doublon avec « Reprendre une course » du flux principal : celle-ci
 * RELANCE une commande (action), celle-là ouvre le détail et montre le montant
 * réglé (consultation). Les deux lisent la même source (`GET /orders` terminées),
 * ce qui garantit qu'elles ne se contredisent jamais.
 */
export function RecentOrdersPanel({ orders }: { orders: Order[] }) {
  const navigate = useNavigate();

  return (
    <section aria-labelledby="aside-recent-orders">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <AppText as="h2" id="aside-recent-orders" variant="bodyBold" color={colors.navy800}>
          Courses récentes
        </AppText>
        <button type="button" onClick={() => navigate(paths.orders())} className="p-1 -m-1">
          <AppText variant="tiny" color={colors.gray500}>
            Voir tout
          </AppText>
        </button>
      </div>

      {orders.length === 0 ? (
        <Card className="flex flex-col items-center px-4 py-6 text-center">
          <span className="mb-2" style={{ fontSize: 24, lineHeight: 1 }} aria-hidden>
            🧾
          </span>
          <AppText as="p" variant="tiny" color={colors.gray500}>
            Vos courses terminées apparaîtront ici.
          </AppText>
        </Card>
      ) : (
      <Card className="overflow-hidden">
        <ul>
          {orders.map((order, index) => (
            <li
              key={order.id ?? index}
              className={index < orders.length - 1 ? "border-b border-gray-100" : undefined}
            >
              <button
                type="button"
                disabled={!order.id}
                onClick={() => order.id && navigate(paths.orderDetail(order.id))}
                className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left transition-colors hover:bg-gray-50 disabled:opacity-60"
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-btn bg-gray-50"
                  style={{ fontSize: 16 }}
                  aria-hidden
                >
                  {isTripOrder(order) ? "🛒" : "🧺"}
                </span>

                <span className="min-w-0 flex-1">
                  <AppText
                    as="span"
                    variant="smallStrong"
                    color={colors.navy800}
                    className="block truncate"
                  >
                    {isTripOrder(order) ? "Course" : "Livraison"}
                  </AppText>
                  <AppText
                    as="span"
                    variant="tiny"
                    color={colors.gray500}
                    className="block truncate"
                  >
                    {subtitle(order)}
                  </AppText>
                </span>

                <span className="shrink-0 text-right">
                  <AppText as="span" variant="tiny" color={colors.gray500} className="block">
                    {relativeDate(order.created_at) ?? ""}
                  </AppText>
                  {/* Vert : le montant d'une course TERMINÉE est un fait réglé,
                      pas une somme en attente. `final_amount` est ce qui a été
                      réellement capturé ; sinon on retombe sur l'estimation,
                      jamais sur zéro. */}
                  <AppText
                    as="span"
                    variant="smallStrong"
                    color={colors.success600}
                    className="block"
                  >
                    {formatXof(order.final_amount ?? order.estimated_amount)}
                  </AppText>
                </span>

                <ChevronRight size={16} color={colors.gray300} aria-hidden className="shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      </Card>
      )}
    </section>
  );
}

/**
 * Sous-ligne : le lieu si on l'a, sinon le contenu de la course.
 *
 * Le contrat ne nomme pas les enseignes — `delivery_address.line1` est ce qui
 * s'en approche le plus. À défaut, les articles disent au moins de quoi il
 * s'agissait.
 */
function subtitle(order: Order): string {
  const place = order.delivery_address?.line1;
  if (typeof place === "string" && place.trim() !== "") return place;

  const count = order.items?.length ?? 0;
  const first = order.items?.[0]?.name?.trim();
  if (first) return count > 1 ? `${first} +${count - 1}` : first;
  return count > 0 ? `${count} article${count > 1 ? "s" : ""}` : "Course";
}

/** « Aujourd'hui », « Hier », puis date courte — même échelle que WalletHistory. */
function relativeDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);

  if (days === 0) return "Aujourd'hui";
  if (days === 1) return "Hier";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}
